import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, Validators, FormArray, FormGroup } from '@angular/forms';
import { notNullOrWhiteSpace, maxLengthInBytes } from '../../../shared/validators';
import * as moment from 'moment';
import { FormDropDownData, FormDropdown, EventType, RuleEventType, ConditionTypes, NotificationCondition, MonitorCriteriaJSON } from '../interface/notification-data';
import { includes } from 'lodash';
import { comparators } from '../../../core';
import { DateRangeFilterValue } from '../../../shared/time-frame-filter/timeframe-filter.component';
import { DatePipe } from '@angular/common';

@Injectable()
export class SampleMonitoringService {

    public frequencyList = [
        {
            key: 1,
            value: 'Immediate',
            isChecked: false
        },
        {
            key: 2,
            value: 'Daily',
            isChecked: false
        },
    ];

    public actionList = [
        {
            key: 1,
            value: 'Email Notification',
            isChecked: false
        },
        {
            key: 2,
            value: 'On-Screen Notification',
            isChecked: false
        },
    ];

    public numberPattern = '^[0-9]*$';
    private regexPattern = '[ A-Za-z0-9_-]+';
    private dateFormat = '[0-9]{2}-[a-zA-z]{3}-[0-9]{4}$';

    constructor(private formBuilder: FormBuilder) {
    }

    public getSampleMonitoringForm(data: any, isCreate = false) {
        let currentDate = new DatePipe('en-US').transform(new Date(), 'dd-MMM-yyyy');
        let frequency = '';
        let effectiveDate = data && data.effectiveDate ? data.effectiveDate : currentDate;
        let expirationDate = data && data.expirationDate ? new DatePipe('en-US').transform(data.expirationDate, 'dd-MMM-yyyy') : '';
        if (isCreate) {
            frequency = 'Immediate';
        }
        return this.formBuilder.group({
            'ruleName': new FormControl(data ? data.ruleName : '', [Validators.required, notNullOrWhiteSpace(), Validators.pattern(this.regexPattern), maxLengthInBytes(50)]),
            'protocol': new FormControl(data ? data.protocol : '', [Validators.required, notNullOrWhiteSpace()]),
            'specimenType': new FormControl(data ? data.specimenType : '', [Validators.required, notNullOrWhiteSpace()]),
            'event': new FormControl(data ? data.event : '', [Validators.required, notNullOrWhiteSpace()]),
            'numberOfDays': new FormControl(data ? data.numberOfDays : '0', [Validators.required, Validators.pattern(this.numberPattern)]),
            'effectiveDate': new FormControl(effectiveDate, [Validators.required, Validators.pattern(this.dateFormat)]),
            'frequency': new FormControl(data ? data.frequency : frequency),
            'expirationDate': new FormControl(expirationDate, [Validators.pattern(this.dateFormat)]),
            'andCondition': new FormArray([])
        }); 
    }

    public getFilteredItems(defaultList: FormDropdown[], myFilterList: FormDropdown[]) {
        let filteredList: FormDropdown[] = [];
        if (!myFilterList || myFilterList.length === 0) { return filteredList; }
        if (!defaultList || defaultList.length === 0) { return filteredList; }
        myFilterList.forEach(t => {
            let index = defaultList.findIndex(item => item.value === t.value);
            if (index !== -1) {
                filteredList.push(t);
            }
        });
        return filteredList;
    }

    public loadDropDownItems(filterData: FormDropDownData[], valueField: string, idField?: string, includeId?: boolean) {
        let selectFilterData: FormDropdown[] = [] = filterData.
            map((sites, index) => {
                return {
                    key: idField ? sites[idField as keyof FormDropDownData] : index + 1,
                    value: includeId ? `${sites[idField as keyof FormDropDownData]}` + ' - ' + `${sites[valueField as keyof FormDropDownData]}` :
                        `${sites[valueField as keyof FormDropDownData]}`,
                    sortId: includeId ? `${sites[idField as keyof FormDropDownData]}` :
                        `${sites[valueField as keyof FormDropDownData]}`
                };
            })
            .filter((data, i, arr) => data.value !== 'null' && arr.findIndex(t => t.value === data.value) === i);
        return this.sortFilterItems(selectFilterData);
    }

    public getDropdownFilterData(portfolioData: FormDropDownData[], selectedItem: FormDropdown[], searchText: string) {
        let filteredPortfolioData = portfolioData;
        if (selectedItem && selectedItem.length) {
            filteredPortfolioData = [];
            selectedItem.map(si => si.value)
                .forEach(name => {
                    filteredPortfolioData
                        .push(...portfolioData
                            .filter(sgpd => sgpd[searchText as keyof FormDropDownData] === name)
                        );
                });
        }
        return filteredPortfolioData;
    }

    public getWeeklyRange(): DateRangeFilterValue {
        return {
            dateBegin: this.formatDateTime(moment().subtract(7, 'd')),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'weekly'
        };
    }

    public sortFilterItems(filterItems: any[]) {
        return filterItems.sort((a, b) => comparators.mixedStringComparator(a.sortId, b.sortId));
    }

    public sortFilterItemsByValue(filterItems: any[]) {
        return filterItems.sort((a, b) => comparators.mixedStringComparator(a.value, b.value));
    }

    public newCondition() {
        return this.formBuilder.group({
            key: '',
            value: '',
        });
    }

    public getConditionsList(eventType: string): FormDropdown[] {
        if (eventType === EventType.cancelledSamples) {
            return this.getCancelledSamplesConditions();
        } 
        return this.getEventConditions();
    }

    public getRuleEventType(eventType: string) {
        if (eventType === EventType.cancelledSamples) {
            return RuleEventType.CancelledEvent;
        }
        if (eventType === EventType.PendingArrivalNonBatch) {
            return RuleEventType.PendingArrivalNonBatch;
        }
        if (eventType === EventType.PendingArrivalBatch) {
            return RuleEventType.PendingArrivalBatch;
        }
        return RuleEventType.ActiveeQueryEvent;
    }

    public getDropDownList(condition: string, criteriaData: FormDropDownData[]) {
        if (condition === ConditionTypes.region) {
            return this.loadDropDownItems(criteriaData, 'region');
        }
        if (condition === ConditionTypes.country) {
            return this.loadDropDownItems(criteriaData, 'countryName', 'countryCode');
        }
        if (condition === ConditionTypes.visitDescription) {
            return this.loadDropDownItems(criteriaData, 'visitDescription');
        }
        if (condition === ConditionTypes.visitCode) {
            return this.loadDropDownItems(criteriaData, 'protocolVisitCode');
        }
        if (condition === ConditionTypes.expectedReceiptCondition) {
            return this.loadDropDownItems(criteriaData, 'expectedReceiptCondition');
        }
        return this.loadDropDownItems(criteriaData, 'investigatorName', 'investigatorNumber', true);
    }

    public addNotificationCritieria(criteria: any, notificationConditions: NotificationCondition[], formGroup: FormGroup, isNonBatchEvent: boolean) {
        criteria = this.addNumbeofDaysToCriteria(criteria, formGroup, isNonBatchEvent);
        if (!notificationConditions.length) { return criteria; }
        notificationConditions.forEach(t => {
            if (t.selectedCondition && t.selectedConditionValue.length > 0) {
                if (t.selectedConditionValue.length !== t.conditionValueList.length) {
                    criteria[t.selectedCondition.criteria] = t.selectedCondition.loadId ? t.selectedConditionValue.map(p => p.key) :
                        t.selectedConditionValue.map(a => a.value);
                } else {
                    criteria[t.selectedCondition.criteria] = [];
                }
            }
        });
        return criteria;
    }

    public addNumbeofDaysToCriteria(criteria: any, formGroup: FormGroup, isNonBatchEvent: boolean) {
        if (!isNonBatchEvent) { return criteria; }
        let daysPending: string = formGroup.get('numberOfDays').value;
        if (!daysPending) { return criteria; }
        criteria.numberOfDays = daysPending;
        return criteria;
    }

    public getFilteredCriteria(notificationConditions: NotificationCondition[], filterData: FormDropDownData[]) {
        if (!notificationConditions && notificationConditions.length === 0) { return filterData; }
        notificationConditions.forEach(t => {
            filterData = this.getFilteredDropDownList(t.selectedCondition, filterData, t.selectedConditionValue);
        });
        return filterData;
    }

    public getRuleData(res: any, criteriaJSON: MonitorCriteriaJSON, eventdata: any, specimenTypes: any[]) {
        return {
            ruleName: res.monitorRuleName,
            protocol: criteriaJSON.protocol.protocolCode,
            specimenType: specimenTypes.map(t => t.value).join(),
            numberOfDays: criteriaJSON.numberOfDays ? criteriaJSON.numberOfDays : '0',
            event: eventdata && eventdata.value,
            frequency: res.emailImmediateFlag === 'T' ? 'Immediate' : 'Daily',
            expirationDate: (res.effectiveEndDate && moment().isBefore(res.effectiveEndDate)) ? this.formatDate(res.effectiveEndDate) : '',
            createdDate: res.createdDate ? this.formatDate(res.createdDate) : '',
            lastEditedDate: res.lastEditedDate ? this.formatDate(res.lastEditedDate) : ''
        };
    }

    private getFilteredDropDownList(selectedCondition: FormDropdown, filterData: FormDropDownData[], selectedValues: FormDropdown[]) {
        if (!selectedCondition || !selectedValues) { return filterData; }
        let data = selectedCondition.loadId ? selectedValues.map(t => t.key) : selectedValues.map(t => t.value);
        if (selectedCondition.value === ConditionTypes.region) {
            return this.getFilteredDataList(filterData, 'region', data);
        }
        if (selectedCondition.value === ConditionTypes.country) {
            return this.getFilteredDataList(filterData, 'countryName', data);
        }
        if (selectedCondition.value === ConditionTypes.visitDescription) {
            return this.getFilteredDataList(filterData, 'visitDescription', data);
        }
        if (selectedCondition.value === ConditionTypes.visitCode) {
            return this.getFilteredDataList(filterData, 'protocolVisitCode', data);
        }
        if (selectedCondition.value === ConditionTypes.expectedReceiptCondition) {
            return this.getFilteredDataList(filterData, 'expectedReceiptCondition', data);
        }
        return this.getFilteredDataList(filterData, 'investigatorNumber', data);
    }

    private getEventConditions(): FormDropdown[] {
        let data = [
            {
                key: 1,
                value: 'Region',
                isChecked: false,
                label: 'Select one or more Regions',
                dropDownField: true,
                showAll: true,
                criteria: 'regions'
            },
            {
                key: 2,
                value: 'Country',
                isChecked: false,
                label: 'Select one or more Countries',
                dropDownField: true,
                showAll: true,
                criteria: 'countries'
            },
            {
                key: 3,
                value: 'Visit Description',
                isChecked: false,
                label: 'Select one or more Visit Description',
                dropDownField: true,
                showAll: true,
                criteria: 'visitDescriptions'
            },
            {
                key: 4,
                value: 'Protocol Visit Code',
                isChecked: false,
                label: 'Select one or more Visit Code',
                dropDownField: true,
                showAll: true,
                criteria: 'protocolVisitCodes'
            },
            {
                key: 5,
                value: 'Expected Receipt Condition',
                isChecked: false,
                label: 'Select one or more conditions',
                dropDownField: true,
                showAll: true,
                criteria: 'expectedReceiptConditions'
            },
            {
                key: 6,
                value: 'Investigator',
                isChecked: false,
                label: 'Select one or more Investigators',
                dropDownField: true,
                showAll: true,
                loadId: true,
                criteria: 'investigators'
            }
        ];
        return data;
    }

    private getCancelledSamplesConditions(): FormDropdown[] {
        let data = [
            {
                key: 1,
                value: 'Region',
                isChecked: false,
                label: 'Select one or more Regions',
                dropDownField: true,
                showAll: true,
                criteria: 'regions'
            },
            {
                key: 2,
                value: 'Country',
                isChecked: false,
                label: 'Select one or more Countries',
                dropDownField: true,
                showAll: true,
                criteria: 'countries'
            },
            {
                key: 3,
                value: 'Visit Description',
                isChecked: false,
                label: 'Select one or more Visit Description',
                dropDownField: true,
                showAll: true,
                criteria: 'visitDescriptions'
            },
            {
                key: 4,
                value: 'Protocol Visit Code',
                isChecked: false,
                label: 'Select one or more Visit Code',
                dropDownField: true,
                showAll: true,
                criteria: 'protocolVisitCodes'
            },
            {
                key: 5,
                value: 'Expected Receipt Condition',
                isChecked: false,
                label: 'Select one or more conditions',
                dropDownField: true,
                showAll: true,
                criteria: 'expectedReceiptConditions'
            },
        ];
        return data;
    }

    private formatDateTime(value: moment.Moment, format: string = 'YYYY-MM-DD', defaultString: string = ''): string {
        return moment.isMoment(value) && moment(value).isValid() ? value.format(format) : defaultString;
    }

    private getFilteredDataList(dataList: FormDropDownData[], columnName: string, columnValues: any[]) {
        const isFilterDefined = columnValues && columnValues.length > 0;
        const sitesFiltered = dataList.filter(s =>
            (!isFilterDefined || (includes(columnValues, s[columnName as keyof FormDropDownData])))
        );
        return sitesFiltered;
    }

    private formatDate(date: any) {
        return new DatePipe('en-US').transform(date, 'dd-MMM-yyyy');
    }
}